"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.recordDecisionSchema = exports.SEVERITY_LABELS = exports.OVERRIDE_KIND_LABELS = exports.STAGE_DECISION_OUTCOME_LABELS = exports.DECISION_NOTE_MAX = exports.DECISION_CHASE = exports.HOLD_EXPIRY_DAYS = exports.SEVERITY_BAND = exports.SEVERITY_TRAILING_MONTHS = exports.SEVERITY_MIN_SCORECARDS = exports.DISAGREEMENT_POINTS = void 0;
exports.isDebriefReason = isDebriefReason;
exports.seatCounts = seatCounts;
exports.roundOutcome = roundOutcome;
exports.roundShares = roundShares;
exports.roundScoreOf = roundScoreOf;
exports.meanOf = meanOf;
exports.isDisagreement = isDisagreement;
exports.severityBand = severityBand;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const reason_codes_1 = require("./reason-codes");
const schemas_1 = require("./schemas");
// S21 — round debrief and stage decisions (DEC-01, DEC-03, F12.01–F12.07). Sources: master
// plan S21 and its planning decisions (DECISIONS-LOG S21 Q1–Q7); E10; Appendix A; §L3;
// §L4 as clarified in S21; §L16; §L19; §L22 N-41–N-47, N-78; §L23.
/** DEC-01: two counting panelists this many points apart on one line is a disagreement. */
exports.DISAGREEMENT_POINTS = 2;
/** §L16: a panelist's severity shows once they have this many scorecards in the window. */
exports.SEVERITY_MIN_SCORECARDS = 10;
exports.SEVERITY_TRAILING_MONTHS = 12;
/** S21 Q3: ±10% either way of everyone's average; nothing is shown between. */
exports.SEVERITY_BAND = 0.1;
/** F12.07 / §L18: a hold turns into a pool prompt after this many days (S22 runs the prompt). */
exports.HOLD_EXPIRY_DAYS = 30;
/**
 * §L4 as clarified in S21 Q7: rungs in working days on the decider's calendar, counted
 * from the moment the round became decidable. N-41 (or N-44) goes at that moment.
 */
exports.DECISION_CHASE = [
    { level: 1, afterReadyWorkingDays: 1, to: 'decider' },
    { level: 2, afterReadyWorkingDays: 2, to: 'decider' },
    { level: 3, afterReadyWorkingDays: 3, to: 'hr_lead' },
];
exports.DECISION_NOTE_MAX = 2000;
exports.STAGE_DECISION_OUTCOME_LABELS = {
    advance: 'Advance',
    reject: 'Reject',
    hold: 'Hold',
};
/**
 * S21 Q4: a judgement about the interview is made on the debrief, where the rule and the
 * missing-scorecard block apply. "Did not attend" has no scorecards to weigh, and the
 * any-stage reasons are not judgements of the interview, so they stay one click.
 */
function isDebriefReason(code) {
    const reason = reason_codes_1.REJECTION_REASONS_BY_CODE.get(code);
    return reason?.stage === 'interview' && code !== 'INT-NOSHOW';
}
function seatCounts(seat) {
    return seat.role !== 'observer';
}
/**
 * F12.02 — §L3 and §L4 as amended by ADR 0077. Pure, so every case is unit-tested. A round whose
 * every counting scorecard was waived is blocked: a round is never decided on nothing.
 */
function roundOutcome(seats) {
    const live = seats.filter((seat) => seatCounts(seat) && seat.status !== 'waived');
    const submitted = live.filter((seat) => seat.status === 'submitted');
    const waitingOn = live.filter((seat) => seat.status !== 'submitted').map((seat) => seat.userId);
    const isBlocked = waitingOn.length > 0 || submitted.length === 0;
    return { roundScore: roundScoreOf(seats), waitingOn, shares: roundShares(seats), verdict: isBlocked ? 'blocked' : 'open' };
}
/**
 * §L3 (C5): each seat's share of the round result. The seats that count are the counting panelists
 * whose latest version carries a mark; observers, waived seats and unsubmitted ones carry nothing.
 * A seat's raw weight is its percent when every counting seat on the panel has one, and 1 for all
 * of them otherwise (equal shares). The shares are the raw weights of the seats that count divided
 * by their total, so a waiver hands its share out to the rest in proportion.
 */
function roundShares(seats) {
    const counting = seats.filter(seatCounts);
    const isWeighted = counting.length > 0 && counting.every((seat) => seat.weightPercent !== null);
    const rawOf = (seat) => (isWeighted ? (seat.weightPercent ?? 0) : 1);
    const scoring = counting.filter((seat) => seat.status === 'submitted' && seat.panelistScore !== null);
    const total = scoring.reduce((sum, seat) => sum + rawOf(seat), 0);
    return seats.map((seat) => ({
        userId: seat.userId,
        share: total > 0 && scoring.includes(seat) ? rawOf(seat) / total : 0,
    }));
}
/** §L3 (C5): Σ(share × mark) over the seats that count, to two decimals; null when none has a mark. */
function roundScoreOf(seats) {
    const shares = roundShares(seats);
    let weighted = 0;
    let isAny = false;
    seats.forEach((seat, index) => {
        const share = shares[index]?.share ?? 0;
        if (share === 0 || seat.panelistScore === null)
            return;
        weighted += share * seat.panelistScore;
        isAny = true;
    });
    return isAny ? round2(weighted) : null;
}
/** One criterion's mean across these seats' scores, ignoring "Not assessed". */
function meanOf(key, seats) {
    const values = seats.map((seat) => seat.scores.get(key)).filter((value) => typeof value === 'number');
    if (values.length === 0)
        return null;
    return round2(values.reduce((sum, value) => sum + value, 0) / values.length);
}
/** DEC-01: counting panelists 2 or more points apart on this line. */
function isDisagreement(key, seats) {
    const values = seats
        .filter((seat) => seatCounts(seat) && seat.status === 'submitted')
        .map((seat) => seat.scores.get(key))
        .filter((value) => typeof value === 'number');
    if (values.length < 2)
        return false;
    return Math.max(...values) - Math.min(...values) >= exports.DISAGREEMENT_POINTS;
}
exports.OVERRIDE_KIND_LABELS = {
    rule: 'the decision rule',
    threshold: 'the pass mark',
    critical: 'a critical line',
};
exports.SEVERITY_LABELS = {
    below: 'scores below average across all interviews',
    above: 'scores above average across all interviews',
};
/** §L16 with S21 Q3's bands: null until the minimum sample, and between the bands. */
function severityBand(panelistMean, globalMean, count) {
    if (count < exports.SEVERITY_MIN_SCORECARDS || globalMean <= 0)
        return null;
    const index = panelistMean / globalMean;
    if (index <= 1 - exports.SEVERITY_BAND + 1e-9)
        return 'below';
    if (index >= 1 + exports.SEVERITY_BAND - 1e-9)
        return 'above';
    return null;
}
// --- Inputs -------------------------------------------------------------------------------------
const poolNameSchema = zod_1.z.string().trim().min(1, 'Give the pool a name.').max(80, 'Pool names can be up to 80 characters.');
/** F12.03–F12.07 in one request: the decision, and whatever it needs. */
exports.recordDecisionSchema = zod_1.z
    .object({
    outcome: zod_1.z.enum(enums_1.STAGE_DECISION_OUTCOMES),
    /** The application's version the decider read — a stale screen is refused, never overwritten. */
    version: zod_1.z.number().int().nonnegative(),
    reasonCode: zod_1.z.string().trim().min(1, 'Pick a reason before continuing.').max(40).optional(),
    note: zod_1.z.string().trim().max(exports.DECISION_NOTE_MAX, `Notes can be up to ${exports.DECISION_NOTE_MAX} characters.`).nullable().default(null),
    poolIds: zod_1.z.array(schemas_1.uuidSchema).max(10).default([]),
    newPoolNames: zod_1.z.array(poolNameSchema).max(5).default([]),
})
    .superRefine((input, ctx) => {
    if (input.outcome === 'reject' && !input.reasonCode)
        ctx.addIssue({ code: 'custom', path: ['reasonCode'], message: 'Pick a reason before continuing.' });
});
function round2(value) {
    return Math.round(value * 100) / 100;
}
//# sourceMappingURL=decisions.js.map